// Landing screen for an English Skill Builder, and the point a student comes back to.
// Toggles into <EngSkillBuilderPlayer> on start.
//
// Simpler than eng-diag-welcome by one whole section: a diagnostic lists its strands, because it
// spans several and the child is about to work through all of them. A Skill Builder is ONE SKILL,
// so there is nothing to list - the skill is the headline instead.
//
// THE YEAR LEVEL IS NOT SHOWN, as everywhere else in the student app. The SKILL is, and should
// be: naming what they are practising is the point of remediation.

import React from "react"
import { Container } from "reactstrap"
import { useNavigate, useParams } from "react-router-dom"
import { EngSkillBuilderPlayer } from "../../modules/eng-diag/components/EngSkillBuilderPlayer"
import {
  getEngSkillBuilderState,
  type EngSkillBuilderState,
} from "../../modules/eng-diag/services/eng-skillbuilder.service"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import { ENG_SB_COPY as COPY } from "../../constants/diagnostics/eng-diag.constants"

/** Roughly how long it takes, for the "about N minutes" line. */
const SECONDS_PER_QUESTION_ESTIMATE = 45

export const EngSkillBuilderWelcomePage: React.FC = () => {
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const activityKey = key || ""

  const [state, setState] = React.useState<EngSkillBuilderState | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [started, setStarted] = React.useState(false)

  const load = React.useCallback(async (signal?: AbortSignal) => {
    const loaded = await getEngSkillBuilderState({ activity_key: activityKey }, signal)
    if (loaded.error) throw new Error(loaded.error)
    setState(loaded)
  }, [activityKey])

  React.useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        await load(controller.signal)
      } catch (e: any) {
        if (!controller.signal.aborted) setError(e?.message || COPY.ERROR_LOAD)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [load])

  const renderHeader = () => (
    <div className="cs-pg-header bg-dark-blue py-4">
      <Container className="custom-wt">
        <div className="d-flex align-items-center">
          <div className="mt-0 border-right me-4" style={{ width: "20%" }}>
            <div
              className="text-white font-size-14 text-uppercase cursor-pointer"
              onClick={() => (started ? setStarted(false) : navigate(-1))}
              role="button"
            >
              <span>
                <i className="fas fa-chevron-left me-1"></i>
              </span>{" "}
              Back
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between flex-grow-1">
            <h6 className="mb-0 font-size-20 text-white w-60 fw-regular">
              {COPY.PLAYER_TITLE}
            </h6>
            <p className="mb-0 text-uppercase luminous-wht">
              <span className="text-yellow me-1">{COPY.SUBJECT_LABEL}</span>{" "}
              {COPY.SHORT_LABEL}
            </p>
          </div>
        </div>
      </Container>
    </div>
  )

  /** One shell for every state, so loading, error and loaded sit in the same place. */
  const shell = (children: React.ReactNode) => (
    <div className="page-wrapper" style={{ backgroundColor: BMD_UI.PAGE_BG }}>
      {renderHeader()}
      <Container className="custom-wt">
        <div
          className="mt-4 mb-5 p-4 p-md-5"
          style={{
            backgroundColor: BMD_UI.CARD_BG,
            borderRadius: BMD_UI.CARD_RADIUS,
            border: BMD_UI.CARD_BORDER,
          }}
        >
          {children}
        </div>
      </Container>
    </div>
  )

  if (loading) return shell(<p className="text-muted mb-0">{COPY.LOADING}</p>)
  if (error || !state) {
    return shell(<p className="text-danger mb-0">{error || COPY.ERROR_LOAD}</p>)
  }

  if (started) {
    return (
      <div className="page-wrapper" style={{ backgroundColor: BMD_UI.PAGE_BG }}>
        {renderHeader()}
        <Container className="custom-wt py-4">
          <EngSkillBuilderPlayer activityKey={activityKey} onFinished={setState} />
        </Container>
      </div>
    )
  }

  const total = state.question_count
  const answered = state.answered_count
  const finished = state.flow_status === "COMPLETED" || state.current_question_key === null
  const remaining = Math.max(0, total - answered)
  const minutes = Math.max(1, Math.round((remaining * SECONDS_PER_QUESTION_ESTIMATE) / 60))
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0

  if (finished) {
    return shell(
      <>
        <h4 className="fw-700 text-dark mb-2">{COPY.FINISH_TITLE}</h4>
        <p className="text-muted mb-0">
          {COPY.FINISH_NOTE}
          {state.awaiting_marking_count > 0 ? ` ${COPY.FINISH_PENDING_NOTE}` : ""}
        </p>
      </>,
    )
  }

  return shell(
    <>
      <h4 className="fw-700 text-dark mb-2">
        {answered > 0 ? COPY.RESUME_TITLE : COPY.WELCOME_TITLE}
      </h4>
      {/* The skill, as the headline. A diagnostic cannot do this - it spans many - and it is the
          one thing that makes this activity make sense to the child. */}
      {state.skill_name ? (
        <p className="text-dark fw-700 font-size-18 mb-2">
          <span className="text-muted fw-regular font-size-14">{COPY.SKILL_PREFIX} </span>
          {state.skill_name}
        </p>
      ) : null}
      <p className="text-muted mb-1">{COPY.WELCOME_NOTE}</p>
      <p className="text-muted">{COPY.SAVE_NOTE}</p>

      <div className="my-4">
        <div className="d-flex justify-content-between font-size-13 text-muted mb-1">
          <span>
            {answered} of {total} {COPY.ANSWERED_SUFFIX}
          </span>
          <span>
            {COPY.TIME_LEFT_PREFIX} {minutes} {COPY.TIME_LEFT_SUFFIX}
          </span>
        </div>
        <div className="progress" style={{ height: "8px" }}>
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${percent}%` }}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>

      <button
        type="button"
        className="btn btn-primary fw-700 px-4"
        onClick={() => setStarted(true)}
      >
        {answered > 0 ? COPY.RESUME : COPY.START}
      </button>
    </>,
  )
}

export default EngSkillBuilderWelcomePage
